import { useEffect, useState } from 'react';
import { ProductTypeEnum } from '@koob/enums';
import useTripStore from '../../store/useTripStore';
import {
  useDays,
  useSelectedType,
  useTripFilters,
} from '../../hooks/useTripStoreSelectors';
import {
  useExperienceField,
  useExperienceNestedField,
} from '../../hooks/useTripDataFields';
import { useTripDayExperiencePricing } from './useTripDayExperiencePricing';
import { useTripDayExperienceExtrasSync } from './useTripDayExperienceExtrasSync';
import { useTripDayExperienceTransitions } from './useTripDayExperienceTransitions';
import { useTripDayExperienceRemoval } from './useTripDayExperienceRemoval';
import { useTripDayExperienceServices } from './useTripDayExperienceServices';
import { useTripDayExperienceExtrasModal } from './useTripDayExperienceExtrasModal';
import type { NotifyFn, Translate } from './types';

interface TripDayExperienceStateParams {
  dayIndex: number;
  date?: any;
  readOnly: boolean;
  registerRemoveHandler?: (...args: any[]) => void;
  onExperienceRemoved?: (info: any) => void;
  registerExperienceWarning?: (...args: any[]) => void;
  t: Translate;
  notify?: NotifyFn;
  onEvent?: (name: string, payload: unknown) => void;
}

// Everything a program day reads and writes, gathered in one place so the
// component is left with drawing it: the store fields, the pricing request,
// the extras, the removal flow and the props of the additional-services modal.
export function useTripDayExperienceState({
  dayIndex,
  date,
  readOnly,
  registerRemoveHandler,
  onExperienceRemoved,
  registerExperienceWarning,
  t,
  notify,
  onEvent,
}: TripDayExperienceStateParams) {
  const tripFilters = useTripFilters();
  const selectedType = useSelectedType();

  // Zustand store
  const days = useDays();
  const experiencesMap = useTripStore((state) => state.experiences);
  const updateDays = useTripStore((state) => state.updateDays);
  const updateDay = useTripStore((state) => state.updateDay);
  const [{ value: experience }, , { setValue: setExperience }] =
    useExperienceField(dayIndex);
  const [{ value: selectedExtras }, , { setValue: setSelectedExtras }] =
    useExperienceNestedField(dayIndex, 'selectedExtras');

  const [showExtrasModal, setShowExtrasModal] = useState(false);
  const [showDayOptions, setShowDayOptions] = useState(false);

  // The modal opens on the program's own services unless a day band asked for
  // that day's: closing it (recommendation flow included) drops the day scope.
  useEffect(() => {
    if (!showExtrasModal) {
      setShowDayOptions(false);
    }
  }, [showExtrasModal]);

  // Every day of a multi-day program renders this component, but exactly one of
  // them speaks for the instance: the first. It owns the pricing request, the
  // removal of the whole range and the verdict sent to the frame.
  const experienceStartIndex = dayIndex - ((experience?.dayIndex ?? 1) - 1);
  const isExperienceFirstDay =
    !!experience && (!experience?.dayIndex || experience.dayIndex === 1);

  const pricing = useTripDayExperiencePricing({
    dayIndex,
    date,
    experience,
    setExperience,
    days,
    tripFilters,
    showDayOptions,
    isExperienceFirstDay,
    experienceStartIndex,
    registerExperienceWarning,
  });
  const { experienceExtras } = pricing;

  useTripDayExperienceExtrasSync({
    dayIndex,
    experience,
    tripFilters,
    priceObject: pricing.priceObject,
    selectedExtras,
    setSelectedExtras,
    notify,
    t,
  });

  const transitions = useTripDayExperienceTransitions({
    dayIndex,
    experience,
    experienceExtras,
    experienceStartIndex,
    days,
    updateDay,
    showExtrasModal,
    setShowExtrasModal,
  });

  const removal = useTripDayExperienceRemoval({
    dayIndex,
    experience,
    experienceStartIndex,
    days,
    experiencesMap,
    updateDays,
    onExperienceRemoved,
    registerRemoveHandler,
    isExperienceFirstDay,
    readOnly,
    onEvent,
  });

  const services = useTripDayExperienceServices({
    dayIndex,
    experience,
    experienceExtras,
    accomodationExtras: pricing.accomodationExtras,
    selectedExtras,
    extrasPrices: pricing.extrasPrices,
    currency: pricing.currency,
    tripFilters,
    experienceStartIndex,
    readOnly,
    t,
  });

  const canDuplicateExperience =
    !readOnly &&
    selectedType === ProductTypeEnum.EXPERIENCE &&
    !!experience?.isRepeatable &&
    experience?.dayIndex === 1;

  const extrasModalProps = useTripDayExperienceExtrasModal({
    dayIndex,
    experience,
    tripFilters,
    days,
    selectedExtras,
    setSelectedExtras,
    pricing,
    updateExtrasForDay: services.updateExtrasForDay,
    transitions,
    modal: { setShowExtrasModal, showExtrasModal, showDayOptions },
    notify,
    t,
  });

  return {
    experience,
    tripFilters,
    pricing,
    services,
    removal,
    canDuplicateExperience,
    extrasModalProps,
    // A read-only day only shows its services: no band offers to change them.
    openDayOptions: readOnly
      ? undefined
      : () => {
          setShowDayOptions(true);
          setShowExtrasModal(true);
        },
    openGlobalOptions: readOnly
      ? undefined
      : () => {
          setShowDayOptions(false);
          setShowExtrasModal(true);
        },
  };
}
